import React, { useState, useRef, useEffect, type ReactNode } from 'react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useWindowSize } from '../../../../../common/utils/use-window-size/index.tsx';
import messages from './messages.tsx';

type Props = {
	helpText: string;
};

// exported for testing
export const shouldCollapseElem = (element?: HTMLElement) =>
	// https://stackoverflow.com/a/52650163
	// check if the element is being line clamped
	element ? element.scrollHeight !== element.offsetHeight : false;

export const RequestTypeHelpText = ({ helpText }: Props) => {
	const { formatMessage } = useIntl();

	// this property sets whether or not the show more button will be visible
	const [shouldCollapse, setShouldCollapse] = useState(false);
	// this property sets whether the text is expanded or collapsed
	const [isOpen, setIsOpen] = useState(false);
	const toggleIsOpen = () => setIsOpen(!isOpen);

	const helpTextRef = useRef<unknown>(undefined);
	const [windowWidth, windowHeight] = useWindowSize();

	useEffect(() => {
		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'HTMLElement | undefined'.
		const newShouldCollapse = shouldCollapseElem(helpTextRef.current);
		setShouldCollapse(newShouldCollapse);
		if (!newShouldCollapse) {
			setIsOpen(false);
		}
	}, [shouldCollapse, helpText, windowWidth, windowHeight]);

	return (
		<>
			<Collapser isOpen={isOpen}>
				{/* @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type '{ current: HTMLElement | undefined; } & (((instance: any) => void) | RefObject<HTMLElement | SVGElement | Component<{}, {}, any>> | undefined)'. */}
				<HelpText isOpen={isOpen} innerRef={helpTextRef}>
					{helpText}
				</HelpText>
			</Collapser>
			{shouldCollapse && (
				<ButtonWrapper>
					<Button spacing="none" appearance="subtle-link" onClick={toggleIsOpen}>
						{formatMessage(isOpen ? messages.showLess : messages.showMore)}
					</Button>
				</ButtonWrapper>
			)}
		</>
	);
};

/*
    The webkit styles enable multi-line text-overflow ellipsis.
    See https://stackoverflow.com/a/13924997
    It's a bit of a hack but it works in most browsers -
    worst case, it will fall back to not having the ellipsis.
*/
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HelpText = styled.p<{
	innerRef: {
		current: HTMLElement | undefined;
	};
	isOpen: boolean;
	children?: ReactNode;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtle'),
		font: token('font.body.UNSAFE_small'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isOpen }) =>
		!isOpen
			? `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    `
			: '',
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Collapser = styled.div<{
	isOpen: boolean;
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>((props) => ({
	marginTop: token('space.100'),
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: props.isOpen ? 'fit-content' : '32px',
}));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div<{
	children?: ReactNode;
}>`
	font: ${token('font.heading.xxsmall')};
	color: ${token('color.text.subtlest')};
	padding: 0;
	margin: 0;
	margin-top: ${token('space.075')};

	button {
		padding: 0;
		margin: 0;
		border: 0;
	}
`;
